import { WORDMARK_PATHS } from "@/components/wordmarkPaths";
import { CANVAS_FONT } from "@/lib/canvasFont";

/**
 * The TRYBEUP wordmark drawn on a canvas (share cards), so an image someone
 * posts carries the same logo as the app instead of "TrybeUP™" typed in the
 * body font (founder, 2026-10-06: "update it everywhere else").
 *
 * The paths are in potrace's space (see wordmarkPaths.ts): the visible box is
 * x 24–6644, y 24–776 after `translate(0,800) scale(0.1,-0.1)`.
 */
const BOX_X = 24;
const BOX_Y = 24;
const BOX_W = 6620;
const BOX_H = 752;

/** Width of the wordmark letters at a given letter height (no ™). */
export function wordmarkWidth(height: number): number {
  return (BOX_W / BOX_H) * height;
}

/**
 * Draws the wordmark plus ™, centred on `cx`, letters from `top` to
 * `top + height`. Falls back to text where Path2D is missing (old WebViews).
 */
export function drawWordmark(
  ctx: CanvasRenderingContext2D,
  cx: number,
  top: number,
  height: number,
  fill: string,
): void {
  // Same proportions as Wordmark.tsx: letters 0.8em, ™ 0.9em, 0.06em gap.
  const em = height / 0.8;
  const tmSize = 0.9 * em;
  ctx.save();
  ctx.fillStyle = fill;
  ctx.font = `600 ${tmSize}px ${CANVAS_FONT}`;
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  const tmWidth = ctx.measureText("™").width;
  const lettersW = wordmarkWidth(height);
  const total = lettersW + 0.06 * em + tmWidth;
  const left = cx - total / 2;

  if (typeof Path2D === "undefined") {
    ctx.font = `bold ${em}px ${CANVAS_FONT}`;
    ctx.textAlign = "center";
    ctx.fillText("TRYBEUP™", cx, top);
    ctx.restore();
    return;
  }

  ctx.save();
  const k = height / BOX_H;
  ctx.translate(left - BOX_X * k, top - BOX_Y * k);
  ctx.scale(k, k);
  ctx.translate(0, 800);
  ctx.scale(0.1, -0.1);
  for (const d of WORDMARK_PATHS) ctx.fill(new Path2D(d));
  ctx.restore();

  // ™ sits at the top right, nudged up like the app's -0.2em.
  ctx.fillText("™", left + lettersW + 0.06 * em, top - 0.2 * em);
  ctx.restore();
}
